import { useCallback, useMemo, memo } from 'react';
import { Link } from 'react-router-dom';
import PropTypes from 'prop-types';
import { Fade } from 'react-awesome-reveal';
import clsx from 'clsx';

// Imports => Constants
import {
  AcBadge,
  DATETIME_FORMATS,
  ICONS,
  KEYS,
  SIZES,
  THEMES,
} from '@opp/ui-kit';

// Imports => Utilities
import {
  AcIsSet,
  AcIsArray,
  AcSanitize,
} from '@opp/ui-kit';
import {
  AcFormatCurrencyAmount,
  AcFormatDate,
  AcFormatInternalURI,
} from '@utils';

// Imports => Atoms
import {
  AcCard,
  AcHeading,
  AcRichContent,
  AcIcon,
  AcRipple,
} from '@opp/ui-kit';
import {withTranslation} from "react-i18next";
import {observer} from "mobx-react-lite";
import {withStore} from "../../stores";
import {AcFormatPaymentMethod, AcGetPaymentMethodIcon, AcGetPaymentMethodImg} from "../../utilities/ac-format-payment-method";

const _CLASSES = {
  MAIN: 'ac-purchase-list-item',
  LINK: 'ac-purchase-list-item__link',
  HEADER: 'ac-purchase-list-item__header',
  BODY: 'ac-purchase-list-item__body',
  FOOTER: 'ac-purchase-list-item__footer',
  ICON: 'ac-purchase-list-item__icon',
  PAYMENT_ICON: 'ac-purchase-list-item__payment-icon',
  TITLE: 'ac-purchase-list-item__title',
  DESCRIPTION: 'ac-purchase-list-item__description',
  AMOUNT: 'ac-purchase-list-item__amount',
  DATETIME: 'ac-purchase-list-item__datetime',
  STATUS: 'ac-purchase-list-item__status',
  UID: 'ac-purchase-list-item__uid',
};

const AcPurchaseListItem = ({ store: { profile } , data = {}, className, t, i18n }) => {

  const { current_partner_currency } = profile;

  const getStatusClassNames = useMemo(() => {
    return clsx(_CLASSES.STATUS);
  }, []);

  const getDatetimeClassNames = useMemo(() => {
    return clsx(_CLASSES.DATETIME);
  }, []);

  const getPaymentIconClassNames = useMemo(() => {
    return clsx(_CLASSES.PAYMENT_ICON);
  }, []);

  const getDescriptionClassNames = useMemo(() => {
    return clsx(_CLASSES.DESCRIPTION);
  }, []);

  const getAmountClassNames = useMemo(() => {
    return clsx(_CLASSES.AMOUNT);
  }, []);

  const getTitleClassNames = useMemo(() => {
    return clsx(_CLASSES.TITLE);
  }, []);

  const getIconClassNames = useCallback((type) => {
    return clsx(_CLASSES.ICON, `${_CLASSES.ICON}--${type}`);
  }, []);

  const getFooterClassNames = useMemo(() => {
    return clsx(_CLASSES.FOOTER);
  }, []);

  const getBodyClassNames = useMemo(() => {
    return clsx(_CLASSES.BODY);
  }, []);

  const getHeaderClassNames = useMemo(() => {
    return clsx(_CLASSES.HEADER);
  }, []);

  const getLinkClassNames = useMemo(() => {
    return clsx(_CLASSES.LINK);
  }, []);

  const getMainClassNames = useMemo(() => {
    return clsx(_CLASSES.MAIN, className);
  }, [className]);

  const getID = useMemo(() => {
    return data && data.uid;
  }, [data]);

  const getPaymentMethodIcon = useMemo(() => {
    if (!AcIsSet(data) || (!AcIsSet(data.payment_method) && !AcIsSet(data.parent_payment_method))) {
      return null;
    }

    return AcGetPaymentMethodImg(t, getPaymentIconClassNames, data.payment_method, data.parent_payment_method);
  }, [data]);

  const getStatusFormatted = useMemo(() => {
    if (!AcIsSet(data) || !AcIsSet(data.created)) return null;

    const { status } = data;

    let label = t('status.' + status, {ns: 'transactions'});
    let theme = null;

    switch (status) {
      case 'created':
      case 'reserved':
      case 'pending':
      case 'planned':
        theme = THEMES.OMEGA_LIGHT;
        break;

      case 'on_hold':
      case 'refunded':
        theme = THEMES.ECHO;
        break;

      case 'chargeback':
        theme = THEMES.BETA_LIGHT;
        break;

      case 'expired':
      case 'cancelled':
      case 'failed':
      case 'revoked':
        theme = THEMES.ERROR;
        break;

      case 'completed':
        theme = THEMES.SUCCESS;
        break;

      default:
    }

    return <AcBadge className={getStatusClassNames} theme={theme}>{label}</AcBadge>;
  }, [data]);

  const getCreatedDateFormatted = useMemo(() => {
    if (!AcIsSet(data) || !AcIsSet(data.created)) return null;

    const created = data.created * 1000;
    const result = AcFormatDate(
      created,
      null,
      DATETIME_FORMATS.FULL_DAY_WITH_YEAR_AND_TIME_SHORT
    );

    return <AcRichContent className={getDatetimeClassNames} content={result} />;
  }, [data]);

  const getDescriptionFormatted = useMemo(() => {
    if (!AcIsSet(data) || !AcIsSet(data.order)) return null;

    const { order } = data;
    let result = [];

    if (
      AcIsSet(order) &&
            AcIsArray(order.products) &&
            order.products.length > 0
    ) {
      const { products } = order;

      result = products.map((p) => p.name);
    }

    result = result.join(', ');
    result = AcSanitize(result);

    return (
      <AcRichContent className={getDescriptionClassNames} content={result} />
    );
  }, [data]);

  const getAmountFormatted = useMemo(() => {
    if (!AcIsSet(data) || !AcIsSet(data.amount)) return null;

    const { amount } = data;

    // todo Fix currency
    const content = AcFormatCurrencyAmount(amount, current_partner_currency, i18n.resolvedLanguage, true);

    return <AcRichContent content={content} className={getAmountClassNames} />;
  }, [data, i18n.resolvedLanguage, current_partner_currency]);

  const getTitleFormatted = useMemo(() => {
    if (!AcIsSet(data)) return null;

    return (
      <AcHeading rank={6} className={getTitleClassNames}>
        {`${t('purchase', 'Purchase', {ns: 'transactions'})}`}
      </AcHeading>
    );
  }, [data]);

  const getIconFormatted = useMemo(() => {
    if (!AcIsSet(data)) return null;

    return (
      <AcIcon icon={ICONS.SHOPPING_CART} className={getIconClassNames('purchase')} />
    );
  }, [data]);

  const getPurchaseDetailRoute = useMemo(() => {
    if (!AcIsSet(getID)) return null;

    const route = AcFormatInternalURI(data, {
      field: KEYS.UID,
      type: KEYS.PURCHASE,
    });

    return route;
  }, [getID, data]);

  return (
    <Fade direction={'up'} duration={300} triggerOnce>
      <AcCard
        className={getMainClassNames}
        id={getID}
        data-payment-method={data && data.payment_method}
      >
        <AcRipple theme={THEMES.SUBTLE} size={SIZES.SMALL} simple />
        <Link
          to={getPurchaseDetailRoute}
          title={`${t('click-for-details-purchase', 'Click to view details of purchase', {ns: 'transactions'})} ${getID}`}
          className={getLinkClassNames}
        >
          <div className={getHeaderClassNames}>
            {getIconFormatted}
            {getTitleFormatted}
            {getAmountFormatted}
          </div>
          <div className={getBodyClassNames}>
            {getDescriptionFormatted}
            {getPaymentMethodIcon}
          </div>
          <div className={getFooterClassNames}>
            {getCreatedDateFormatted}
            {getStatusFormatted}
          </div>
        </Link>
      </AcCard>
    </Fade>
  );
};

AcPurchaseListItem.propTypes = {
  /**
     * For CSS customization
     */
  className: PropTypes.string,
};

export default withTranslation(['common', 'transactions'])(withStore(observer(AcPurchaseListItem)));
